import { useState, useCallback, useMemo, useEffect } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import Toolbar from './components/Toolbar';
import GridDashboard from './components/GridDashboard';
import { CardTypeSelectorModal } from './components/CardTypeSelectorModal';
import { findAvailablePosition } from './utils/gridLayout';
import { useRafWindowSize } from './hooks/useSize';
import { CARD_TYPES, getDefaultCardData } from './cardTypes';
import useMyBadgeAssignments from 'src/my-rewards/hooks/useMyBadgeAssignments.js';
import { useSelector, useDispatch } from 'react-redux';
import { createSetCurrentUserAction } from 'src/_shared/user/userActions.js';
import { SearchBanner } from 'src/_shared/components/search-banner/index.js';
import { ml, searchOpenSearchNetwork } from 'src/_shared/services/utils.js';
import { useUserStatsAndRankings } from 'src/_shared/hooks/useUserStatsAndRankings.js';
import { queryAnnouncements } from 'src/new-announcements/graphqlQueries.js';
import { MODERATOR_ROLES } from 'src/new-announcements/constants.js';

const QUERY_BADGE_ASSIGNMENTS_BY_FEED = gql`
	query QueryBadgeAssignmentsByFeed(
		$companyId: ID!
		$feedType: FeedType!
		$subCompanyId: ID
		$limit: Int
		$nextToken: String
	) {
		queryBadgeAssignmentsByFeed(
			companyId: $companyId
			feedType: $feedType
			subCompanyId: $subCompanyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				approvalStatus
				awardedBy
				awardedByUser {
					id
					firstName
					lastName
					avatarSignedUrl
					departmentId
					userGroupId
					subCompanyId
				}
				id
				badgeId
				badge {
					id
					name
					badgeType {
						code
					}
					badgeTypeId
					color
					description
					gradientColor
					iconId
					icon {
						code
					}
					image
					label
					isAssignmentRestricted
					shape
					badgeRotation
					customImageKey
					customImageSignedUrl
					labelColor
					customIconKey
					customIconSignedUrl
					category
				}
				content {
					message
					gifURL
					tags
				}
				recipients {
					accountClaimId
					accountClaim {
						id
						firstName
						lastName
						emailAddress
						subCompany
						department
						employeeId
						userId
					}
					userId
					user {
						id
						firstName
						lastName
						departmentId
						subCompanyId
						userGroupId
					}
				}
				companyId
				createdAt
			}
			nextToken
		}
	}
`;

const HISTORY_LIMIT = 100;

export function CustomizableDashboardComponent({ sidebarOpen = false }) {
	const apolloClient = useApolloClient();
	const currentUser = useSelector((state) => state.user.currentUser);
	const dispatch = useDispatch();
	const setCurrentUser = (user) => {
		dispatch(createSetCurrentUserAction(user));
	};
	const departments = [...(currentUser?.company?.departments || [])]
		.sort((a, b) => a.name.localeCompare(b.name))
		.filter((d) => d.active && d.name.trim() !== '');
	const subCompanies = [...(currentUser?.company?.subCompanies || [])].sort(
		(a, b) => a.name.localeCompare(b.name)
	);
	const jobNotificationSetting =
		typeof currentUser?.jobNotificationSetting === 'string'
			? JSON.parse(currentUser.jobNotificationSetting)
			: currentUser?.jobNotificationSetting || {};
	const hideSubCompany =
		currentUser.company?.hideSubcompanyOnJobs || subCompanies.length === 0;
	const initialDashboard =
		typeof currentUser?.company?.customDashboard === 'string'
			? JSON.parse(currentUser.company.customDashboard)
			: {};
	const allMultiLingualData = useSelector(
		(state) => state.dashboard.allMultiLingualData
	);
	const [announcements, setAnnouncements] = useState([]);
	const [hotSearchedJobs, setHotSearchedJobs] = useState([]);
	const [recognitionShoutouts, setRecognitionShoutouts] = useState([]);

	const { userStats, leaderboards, userStatsLoading } = useUserStatsAndRankings(
		currentUser?.id
	);

	const { badgeList: myRewards } = useMyBadgeAssignments(
		currentUser,
		false,
		() => {}
	);

	const [config, setConfig] = useState({
		horizontalMargin: 16,
		verticalMargin: 16,
		rowHeight: 230,
		columnWidth: 200,
		breakpoints: [
			{ name: 'Desktop', minWidth: 1200, columns: 3 },
			{ name: 'Tablet', minWidth: 768, columns: 2 },
			{ name: 'Mobile', minWidth: 480, columns: 1 },
			{ name: 'Small', minWidth: 0, columns: 1 },
		],
		responsiveWidth: true,
		enableDashboardSearch: true,
		...initialDashboard?.config,
	});

	const [isEditMode, setIsEditMode] = useState(false);
	const [showCardTypeSelector, setShowCardTypeSelector] = useState(false);
	const isAdmin =
		currentUser.role === 'admin' || currentUser.role === 'superAdmin';
	// Admins previewing as an employee see what an employee sees, placeholder
	// sample stats included.
	const isAdminView = isAdmin && currentUser?.displayAs === 'admin';

	const [cards, setCards] = useState(
		initialDashboard.cards || [
			{
				id: '1',
				x: 0,
				y: 0,
				width: 1,
				height: 1,
				type: CARD_TYPES.CALL_TO_ACTION,
				data: getDefaultCardData(CARD_TYPES.CALL_TO_ACTION),
			},
			{
				id: '2',
				x: 1,
				y: 0,
				width: 1,
				height: 1,
				type: CARD_TYPES.CALL_TO_ACTION_REFERRAL,
				data: getDefaultCardData(CARD_TYPES.CALL_TO_ACTION_REFERRAL),
			},
			{
				id: '3',
				x: 2,
				y: 0,
				width: 1,
				height: 1,
				type: CARD_TYPES.CALL_TO_ACTION_GENERAL,
				data: getDefaultCardData(CARD_TYPES.CALL_TO_ACTION_GENERAL),
			},
			{
				id: '4',
				x: 0,
				y: 1,
				width: 1,
				height: 1,
				type: CARD_TYPES.CALL_TO_ACTION_LEAD,
				data: getDefaultCardData(CARD_TYPES.CALL_TO_ACTION_LEAD),
			},
			{
				id: '5',
				x: 1,
				y: 1,
				width: 1,
				height: 1,
				type: CARD_TYPES.CALL_TO_ACTION_INTERNAL,
				data: getDefaultCardData(CARD_TYPES.CALL_TO_ACTION_INTERNAL),
			},
			{
				id: '6',
				x: 2,
				y: 1,
				width: 1,
				height: 1,
				type: CARD_TYPES.JOB_ALERTS,
				data: getDefaultCardData(CARD_TYPES.JOB_ALERTS),
			},
			{
				id: '7',
				x: 0,
				y: 2,
				width: 1,
				height: 1,
				type: CARD_TYPES.LEADERBOARD,
				data: getDefaultCardData(CARD_TYPES.LEADERBOARD),
			},
			{
				id: '8',
				x: 1,
				y: 2,
				width: 1,
				height: 1,
				type: CARD_TYPES.YOUR_REFERRALS,
				data: getDefaultCardData(CARD_TYPES.YOUR_REFERRALS),
			},
			{
				id: '9',
				x: 2,
				y: 2,
				width: 1,
				height: 1,
				type: CARD_TYPES.REFERRALS_RANKING,
				data: getDefaultCardData(CARD_TYPES.REFERRALS_RANKING),
			},
			{
				id: '10',
				x: 0,
				y: 3,
				width: 1,
				height: 1,
				type: CARD_TYPES.POINTS_RANKING,
				data: getDefaultCardData(CARD_TYPES.POINTS_RANKING),
			},
			{
				id: '11',
				x: 1,
				y: 3,
				width: 1,
				height: 1,
				type: CARD_TYPES.CUSTOM_HTML,
				data: getDefaultCardData(CARD_TYPES.CUSTOM_HTML),
			},
			{
				id: '12',
				x: 2,
				y: 3,
				width: 1,
				height: 1,
				type: CARD_TYPES.ANNOUNCEMENTS,
				data: getDefaultCardData(CARD_TYPES.ANNOUNCEMENTS),
			},
			{
				id: '13',
				x: 0,
				y: 4,
				width: 1,
				height: 1,
				type: CARD_TYPES.RECENT_JOBS,
				data: getDefaultCardData(CARD_TYPES.RECENT_JOBS),
			},
		]
	);

	const [currentHistoryState, setCurrentHistoryState] = useState({
		config,
		cards,
	});
	const [undoStack, setUndoStack] = useState([]);
	const [redoStack, setRedoStack] = useState([]);

	useEffect(() => {
		const newDashboard =
			typeof currentUser?.company?.customDashboard === 'string'
				? JSON.parse(currentUser.company.customDashboard)
				: {};
		if (newDashboard.cards) {
			setCards(newDashboard.cards);
		}
		setConfig((prev) => ({
			...prev,
			...newDashboard?.config,
		}));
	}, [currentUser?.company?.customDashboard]);

	// Fetch more than any tile can display so pinned items (which the resolver
	// sorts to the front) can't push recent announcements out of the window.
	const ANNOUNCEMENT_FETCH_SIZE = 25;
	// Use the same role-aware query as the Announcements page so the dashboard
	// tiles never surface an announcement the user can't open there. For
	// non-moderators the resolver excludes hidden, unpublished, expired, and
	// group-restricted announcements.
	const isAnnouncementModerator = MODERATOR_ROLES.includes(
		currentUser?.displayAs
	);
	useEffect(() => {
		const fetchAnnouncements = async () => {
			if (!currentUser?.id) {
				return;
			}

			try {
				const result = await apolloClient.query({
					query: queryAnnouncements,
					variables: {
						userId: currentUser.id,
						overrideRole: !isAnnouncementModerator || undefined,
						first: ANNOUNCEMENT_FETCH_SIZE,
					},
					fetchPolicy: 'network-only',
				});

				const items = result.data?.queryAnnouncements?.items;
				if (items) {
					// The resolver always orders pinned announcements first. Dashboard
					// tiles are meant to show the most recent, so re-sort by date here.
					const byNewest = [...items].sort(
						(a, b) =>
							new Date(b.dateCreated || 0) - new Date(a.dateCreated || 0)
					);
					setAnnouncements(byNewest);
				}
			} catch (error) {
				console.error('Error fetching announcements:', error);
			}
		};

		fetchAnnouncements();
	}, [apolloClient, currentUser?.id, isAnnouncementModerator]);

	useEffect(() => {
		const fetchHotJobs = async () => {
			if (!currentUser?.companyId) {
				return;
			}

			try {
				const isExtendedUser = currentUser?.role === 'extendedUser';
				const extendedCompaniesData = currentUser?.extendedCompaniesData || [];
				const extendedCompanies = extendedCompaniesData.filter(
					(company) => company?.disableExtendedUserJobsView !== true
				);
				const comps =
					extendedCompanies && extendedCompanies.length > 0 && isExtendedUser
						? extendedCompanies
						: [currentUser?.company];
				const companies = comps.map((company) => company?.id);

				const unit =
					(currentUser?.userGroup?.measurement || 'imperial') === 'metric'
						? 'km'
						: 'mi';

				const parametersHot = {
					query: '',
					size: 300,
					coordinates: [{ lat: null, lng: null }],
					role: currentUser?.role,
					currentUser: currentUser?.id,
					filters: {
						companies: companies,
						status: 'open',
						isHotJob: true,
					},
					unit,
				};

				const responseHot = await searchOpenSearchNetwork(
					parametersHot,
					'erin-jobs'
				);

				if (responseHot?.data) {
					setHotSearchedJobs(responseHot.data);
				}
			} catch (error) {
				console.error('Error fetching hot jobs:', error);
			}
		};

		fetchHotJobs();
	}, [currentUser?.companyId, currentUser?.role, currentUser?.id]);

	useEffect(() => {
		const fetchRecognitionShoutouts = async () => {
			if (!currentUser?.companyId) {
				return;
			}

			const recognitionCard = cards.find(
				(card) => card.type === CARD_TYPES.RECOGNITION_SHOUTOUTS
			);
			const limit = recognitionCard?.data?.shoutoutsLimit || 3;

			try {
				const result = await apolloClient.query({
					query: QUERY_BADGE_ASSIGNMENTS_BY_FEED,
					variables: {
						companyId: currentUser.companyId,
						feedType: 'newest',
						limit: limit,
					},
					fetchPolicy: 'network-only',
				});

				if (result.data?.queryBadgeAssignmentsByFeed?.items) {
					setRecognitionShoutouts(
						result.data.queryBadgeAssignmentsByFeed.items
					);
				}
			} catch (error) {
				console.error('Error fetching recognition shoutouts:', error);
			}
		};

		fetchRecognitionShoutouts();
	}, [apolloClient, currentUser?.companyId, cards]);

	const { width: viewportWidth } = useRafWindowSize();
	const currentColumns = useMemo(() => {
		const sidebarWidth = 260;
		const containerPadding = 30;
		const userMargin = config.horizontalMargin * 2;
		const breakpointAdjustment =
			(sidebarOpen ? 0 : sidebarWidth) - userMargin - containerPadding;

		for (const bp of config.breakpoints) {
			const adjustedMinWidth = Math.max(0, bp.minWidth + breakpointAdjustment);
			if (viewportWidth >= adjustedMinWidth) return bp.columns;
		}
		return config.breakpoints[config.breakpoints.length - 1]?.columns ?? 1;
	}, [config.breakpoints, viewportWidth, sidebarOpen, config.horizontalMargin]);

	const hasStateChanged = (a, b) =>
		a.config !== b.config || a.cards !== b.cards;

	const pushUndo = useCallback((prevState) => {
		setUndoStack((prev) => {
			const next = [...prev, prevState];
			return next.length > HISTORY_LIMIT
				? next.slice(next.length - HISTORY_LIMIT)
				: next;
		});
	}, []);

	const handleUndoHistory = useCallback(
		({ newHistoryState, isUndo = false, isRedo = false }) => {
			if (!hasStateChanged(currentHistoryState, newHistoryState)) return;

			const lastUndo = undoStack[undoStack.length - 1] ?? null;
			const lastRedo = redoStack[redoStack.length - 1] ?? null;

			if (!isUndo && !isRedo) {
				if (!lastUndo || hasStateChanged(lastUndo, currentHistoryState)) {
					pushUndo(currentHistoryState);
				}
				setRedoStack([]);
			}

			if (isRedo) {
				if (!lastUndo || hasStateChanged(lastUndo, currentHistoryState)) {
					pushUndo(currentHistoryState);
				}
				setRedoStack((prev) => prev.slice(0, -1));
				setConfig(newHistoryState.config);
				setCards(newHistoryState.cards);
			}

			if (isUndo) {
				setUndoStack((prev) => prev.slice(0, -1));
				setRedoStack((prev) => {
					const base =
						lastRedo && !hasStateChanged(lastRedo, currentHistoryState)
							? prev
							: [...prev, currentHistoryState];
					return base;
				});
				setConfig(newHistoryState.config);
				setCards(newHistoryState.cards);
			}

			setCurrentHistoryState(newHistoryState);
		},
		[currentHistoryState, undoStack, redoStack, pushUndo]
	);

	const handleConfigChange = useCallback(
		(newConfig, isCompletedAction = false) => {
			setConfig(newConfig);
			if (isCompletedAction) {
				handleUndoHistory({ newHistoryState: { config: newConfig, cards } });
			}
		},
		[cards, handleUndoHistory]
	);

	const handleCardsChange = useCallback(
		(newCards, isCompletedAction = false) => {
			setCards(newCards);
			if (isCompletedAction) {
				handleUndoHistory({ newHistoryState: { config, cards: newCards } });
			}
		},
		[config, handleUndoHistory]
	);

	const undo = useCallback(() => {
		const next = undoStack.length
			? undoStack[undoStack.length - 1]
			: currentHistoryState;
		handleUndoHistory({ newHistoryState: next, isUndo: true });
	}, [undoStack, currentHistoryState, handleUndoHistory]);

	const redo = useCallback(() => {
		const next = redoStack.length
			? redoStack[redoStack.length - 1]
			: currentHistoryState;
		handleUndoHistory({ newHistoryState: next, isRedo: true });
	}, [redoStack, currentHistoryState, handleUndoHistory]);

	const openCardTypeSelector = useCallback(() => {
		setShowCardTypeSelector(true);
	}, []);

	const closeCardTypeSelector = useCallback(() => {
		setShowCardTypeSelector(false);
	}, []);

	const addCard = useCallback(
		(cardType) => {
			const { x, y } = findAvailablePosition(cards, 1, 1, currentColumns);
			const newCard = {
				id: `card-${Date.now()}`,
				x,
				y,
				width: 1,
				height: 1,
				type: cardType,
				data: getDefaultCardData(cardType),
			};
			handleCardsChange([...cards, newCard], true);
		},
		[cards, currentColumns, handleCardsChange]
	);

	const handleUpdateCardData = useCallback(
		(cardId, newData) => {
			const updatedCards = cards.map((card) =>
				card.id === cardId ? { ...card, data: newData } : card
			);
			handleCardsChange(updatedCards, true);
		},
		[cards, handleCardsChange]
	);

	const toggleEditMode = useCallback(() => {
		setIsEditMode((prev) => !prev);
	}, []);

	return (
		<div className="h-screen flex flex-col" style={{ padding: '0 16px' }}>
			{config?.enableDashboardSearch && (
				<div style={{ margin: '0 -16px' }}>
					<SearchBanner
						imageS3Data={currentUser?.company?.dashboardSearchBG}
						helloText={ml(`Hello`, currentUser, allMultiLingualData)}
						firstName={currentUser?.firstName}
						companyId={currentUser?.companyId}
						callToAction={
							<p>
								{ml('Find jobs at', currentUser, allMultiLingualData)}{' '}
								<strong style={{ display: 'inline-block' }}>
									{currentUser?.company?.name}
								</strong>
							</p>
						}
						placeholder={ml(
							'Search by location, job name and/or keywords',
							currentUser,
							allMultiLingualData
						)}
					/>
				</div>
			)}
			{isAdmin && currentUser?.displayAs === 'admin' && (
				<div className="mb-4">
					<Toolbar
						config={config}
						onConfigChange={handleConfigChange}
						onAddCard={openCardTypeSelector}
						cards={cards}
						onCardsChange={handleCardsChange}
						onUndo={undo}
						onRedo={redo}
						canUndo={undoStack.length > 0}
						canRedo={redoStack.length > 0}
						isEditMode={isAdmin && isEditMode}
						onToggleEditMode={toggleEditMode}
						currentUser={currentUser}
						setCurrentUser={setCurrentUser}
					/>
					<CardTypeSelectorModal
						isOpen={showCardTypeSelector}
						onClose={closeCardTypeSelector}
						onSelectCardType={addCard}
						existingCards={cards}
					/>
				</div>
			)}
			<GridDashboard
				config={config}
				cards={cards}
				onCardsChange={handleCardsChange}
				onUpdateCardData={handleUpdateCardData}
				isEditMode={isAdmin && isEditMode}
				currentColumns={currentColumns}
				userStats={userStats}
				announcements={announcements}
				leaderboards={leaderboards}
				userStatsLoading={userStatsLoading}
				isAdminView={isAdminView}
				currentUser={currentUser}
				setCurrentUser={setCurrentUser}
				departments={departments}
				subCompanies={subCompanies}
				jobNotificationSetting={jobNotificationSetting}
				hideSubCompany={hideSubCompany}
				hotSearchedJobs={hotSearchedJobs}
				recognitionShoutouts={recognitionShoutouts}
				myRewards={myRewards}
			/>
		</div>
	);
}
